/**
 * useBacktest -- Zustand store for backtesting engine.
 */
import { create } from 'zustand'
import { runBacktest, getBacktestStrategies, getBacktestPresets } from '@/lib/api'
import type { BacktestConfig, BacktestResult, BacktestStrategy, BacktestPreset } from '@/lib/types'

interface BacktestState {
  result: BacktestResult | null
  strategies: BacktestStrategy[]
  presets: BacktestPreset[]
  running: boolean
  loadingStrategies: boolean
  loadingPresets: boolean
  error: string | null
  run: (config: BacktestConfig) => Promise<void>
  fetchStrategies: () => Promise<void>
  fetchPresets: () => Promise<void>
  clear: () => void
}

export const useBacktest = create<BacktestState>()((set) => ({
  result: null,
  strategies: [],
  presets: [],
  running: false,
  loadingStrategies: false,
  loadingPresets: false,
  error: null,

  run: async (config) => {
    set({ running: true, error: null })
    try {
      const data = await runBacktest(config)
      set({ result: data, running: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Backtest failed'
      set({ running: false, error: msg })
    }
  },

  fetchStrategies: async () => {
    set({ loadingStrategies: true })
    try {
      const { strategies } = await getBacktestStrategies()
      set({ strategies, loadingStrategies: false })
    } catch { set({ loadingStrategies: false }) }
  },

  fetchPresets: async () => {
    set({ loadingPresets: true })
    try {
      const { presets } = await getBacktestPresets()
      set({ presets, loadingPresets: false })
    } catch { set({ loadingPresets: false }) }
  },

  clear: () => set({ result: null, error: null }),
}))
